import { useField, useForm } from "@taskfortress/core/src/form"
import {
  validateEmail,
  validateMasterKey,
  validatePassword,
} from "@taskfortress/core/src/form/validation"
import { useCallback, useState } from "react"
import { Alert } from "react-native"
import { useAuth } from "../auth"
import { BaseScreen, Button, Text, TextLink } from "../components"
import { Box } from "../components/Box"
import { AuthForm, AuthLayout } from "../components/auth"
import { TextInput } from "../components/form"

export const LoginScreen = ({ navigation }: any) => {
  const [isLoading, setIsLoading] = useState(false)
  const { login } = useAuth()

  // Form fields
  const emailField = useField({
    name: "email",
    initialValue: "",
    validate: validateEmail,
  })

  const passwordField = useField({
    name: "password",
    initialValue: "",
    validate: validatePassword,
  })

  const masterKeyField = useField({
    name: "masterKey",
    initialValue: "",
    validate: validateMasterKey,
  })

  // Form management
  const form = useForm({
    fields: [emailField, passwordField, masterKeyField],
  })

  const handleLogin = useCallback(async () => {
    if (!form.valid) {
      Alert.alert("Error", "Please fix the errors in the form")
      return
    }

    setIsLoading(true)
    try {
      const success = await login(
        emailField.value!,
        passwordField.value!,
        masterKeyField.value!,
      )
      if (!success) {
        Alert.alert("Login Failed", "Invalid credentials. Please try again.")
      }
    } catch (error) {
      console.error("Login error:", error)
      Alert.alert("Login Failed", "An error occurred during login.")
    } finally {
      setIsLoading(false)
    }
  }, [
    form.valid,
    emailField.value,
    passwordField.value,
    masterKeyField.value,
    login,
  ])

  const handleSignup = useCallback(async () => {
    navigation.navigate("Signup")
  }, [navigation])

  const handleResetPassword = useCallback(async () => {
    navigation.navigate("ForgotPassword")
  }, [navigation])

  return (
    <BaseScreen>
      <AuthLayout>
        <Text className="text-3xl font-bold mb-2 text-center">
          TaskFortress
        </Text>
        <Text className="text-lg text-typography-500 mb-8 text-center">
          Login to your account
        </Text>

        <AuthForm onSubmit={handleLogin}>
          <TextInput
            name="email"
            label="Email"
            placeholder="abc@example.com"
            value={emailField.value}
            handleChange={emailField.handleChange}
            error={emailField.error}
            disabled={isLoading}
          />

          <TextInput
            name="password"
            label="Password"
            placeholder="********"
            value={passwordField.value}
            handleChange={passwordField.handleChange}
            error={passwordField.error}
            type="password"
            disabled={isLoading}
          />

          <TextInput
            name="masterKey"
            label="Master Key"
            placeholder="ABCD-1234-..."
            value={masterKeyField.value}
            handleChange={masterKeyField.handleChange}
            error={masterKeyField.error}
            type="password"
            disabled={isLoading}
          />

          <Button
            onPress={handleLogin}
            disabled={isLoading || !form.valid}
            isLoading={isLoading}
          >
            Login
          </Button>

          <Box className="flex-row justify-between">
            <TextLink onPress={handleResetPassword} disabled={isLoading}>
              Forgot Password?
            </TextLink>

            <TextLink onPress={handleSignup} disabled={isLoading}>
              Create Account
            </TextLink>
          </Box>
        </AuthForm>
      </AuthLayout>
    </BaseScreen>
  )
}
